import React from 'react';
import { Box, Typography, Grid, Card, Chip } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface Attendee {
  id: string;
  familyMember?: {
    name: string;
    relationship?: string;
  } | null;
  // Plan 4.10 — guest attendees populate eventGuest instead of familyMember.
  eventGuest?: {
    firstName: string;
    lastName: string;
    relationship?: string;
  } | null;
  checkedIn?: boolean;
}

interface QRCodeDisplayProps {
  registrationId?: string;
  attendees?: Attendee[];
  eventTitle: string;
  size?: number;
}

const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({ registrationId, attendees, eventTitle, size = 200 }) => {
  // Debug: Log what we received to help diagnose QR code issues
  console.log('[QRCodeDisplay] Props:', {
    registrationId,
    attendeesCount: attendees?.length ?? 0,
    attendees: attendees?.map(a => ({ id: a.id, name: a.familyMember?.name, checkedIn: a.checkedIn }))
  });

  // If attendees provided, show per-attendee QR codes
  if (attendees && attendees.length > 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {eventTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          One QR code per attendee - show the relevant code at check-in
        </Typography>
        <Grid container spacing={2} justifyContent="center">
          {attendees.map((attendee) => (
            <Grid item key={attendee.id} xs={12} sm={6} md={attendees.length === 1 ? 12 : 6}>
              <Card variant="outlined" sx={{ p: 2, position: 'relative' }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {attendee.familyMember
                    ? attendee.familyMember.name
                    : attendee.eventGuest
                      ? `${attendee.eventGuest.firstName} ${attendee.eventGuest.lastName}`.trim()
                      : 'Unknown'}
                </Typography>
                {attendee.checkedIn ? (
                  <Box sx={{ py: 3 }}>
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Checked In"
                      color="success"
                      sx={{ fontSize: '1rem', py: 2 }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ display: 'inline-block', p: 1, backgroundColor: '#ffffff', borderRadius: 2 }}>
                    <QRCodeSVG
                      value={attendee.id}
                      size={size}
                      level="M"
                      includeMargin
                    />
                  </Box>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Legacy: single registration QR code
  return (
    <Box sx={{ textAlign: 'center', p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {eventTitle}
      </Typography>
      <Box sx={{ display: 'inline-block', p: 2, backgroundColor: '#ffffff', borderRadius: 2 }}>
        <QRCodeSVG
          value={registrationId || ''}
          size={size}
          level="M"
          includeMargin
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Show this QR code at check-in
      </Typography>
    </Box>
  );
};

export default QRCodeDisplay;
